import { startTransition, useState } from "react";
import { useNavigate } from "react-router";
import { t } from "@wr/i18n";
import {
  BottomSheet,
  Button,
  CheckIcon,
  CountdownAction,
  FormBanner,
  Mascot,
  type BottomSheetProps,
} from "@wr/ui";
import { ConversionError, convertGuest, resendVerification } from "../lib/auth";
import { useSession } from "../app/SessionProvider";
import { withNext } from "../app/routing";
import {
  CredentialsFields,
  emailLooksValid,
  passwordLongEnough,
  useCredentials,
} from "./CredentialsFields";
import { LegalAttestation } from "./LegalAttestation";

type Phase = "idle" | "submitting" | "email-taken" | "success";

export interface ConversionSheetProps {
  /**
   * Where the player was when they asked to convert — `location.pathname +
   * location.search`, captured by `OverlayProvider` at the moment this
   * component mounts (§14.1 r1, LOCKED). It is a prop and not a hook call
   * because reading the location *here*, at any point after mount, is exactly
   * the bug the requirement exists to prevent.
   */
  returnTo: string;
  /**
   * Abandonment (§14, LOCKED): the player dismissed the *form*. Nothing has
   * been written, the guest is untouched, focus goes back to the trigger.
   */
  onClose: () => void;
  /**
   * The success panel's exit (§14.1 r4).
   *
   * Deliberately **not** `onClose`: r3 removes every dismissal from the success
   * state, so "close the sheet" is not a thing that can happen there — what
   * happens is a handoff, and taking the overlay down is one of the two things
   * it consists of. QA T-175 B1 was exactly this callback missing: the handoff
   * navigated and left the panel (and its static scrim, and the scroll lock)
   * mounted over the login screen it had just navigated to, with r3 having
   * correctly removed every other way out.
   */
  onHandoff: () => void;
  /**
   * **T-417 NB2 — the surviving anchor for the abandonment path (§22.20's
   * spirit).**
   *
   * Three of the four entry points are inside an overlay, and §12's one-overlay
   * rule unmounts that overlay in the same commit this sheet mounts: the
   * control the player actually tapped is gone before there is anything to
   * restore focus to. QA measured the consequence live — after `Esc` the sheet
   * is gone and `document.activeElement.tagName === "BODY"`, at 390 and at
   * 1280, so a converting player lands at the top of the document.
   *
   * The provider passes the control that owned focus when the overlay **chain**
   * began (the top-bar avatar, the hamburger, or the `/welcome` card). Used
   * only when the recorded trigger cannot take focus back — when it can, the
   * ordinary §15.6 restore is unchanged.
   */
  restoreFocusTo?: BottomSheetProps["restoreFocusTo"];
}

/**
 * Guest → registered (design spec §14, amended by §14.1 ruling A).
 *
 * **LOCKED, and all of them matter:**
 *
 * 1. **Not a route.** A bottom sheet on mobile, a dialog on desktop, over
 *    whatever screen the player was on. Converting is not a place you go.
 * 2. **Same components as the sign-up tab.** `CredentialsFields` is literally
 *    shared — a conversion *is* a sign-up with a different frame.
 * 3. **Abandonment is free.** Dismissing leaves the guest account untouched
 *    (nothing is written until the request runs), returns focus to the
 *    trigger, and produces no nag and no second prompt in the same session.
 *
 * ── The `success` state is a handoff, not a landing (§14.1 ruling A) ────────
 *
 * T-168 revokes the guest session on a successful conversion, because the new
 * registered account cannot be signed in until it verifies. Ivy's amendment
 * scopes the "lands back exactly where they were" LOCK to the **form phase**
 * (`idle` / `submitting` / `email-taken`) and the abandonment path, and makes
 * success a deliberate, player-tapped handoff whose promise is kept on the far
 * side of verification via `?next=`:
 *
 *  - the page underneath **never visibly navigates** — the session refetch and
 *    every route guard stand down while `justConverted` is set (r2);
 *  - the panel is **non-dismissible** — no scrim, no `Esc`, no close, no focus
 *    return to a trigger that is on a screen this player is no longer
 *    authenticated for (r3);
 *  - its only exit is the primary CTA to `/login?next=<captured target>` (r4),
 *    and that exit **takes the overlay with it** — see `handOffToLogin`.
 */
export function ConversionSheet({
  returnTo,
  onClose,
  onHandoff,
  restoreFocusTo,
}: ConversionSheetProps) {
  const navigate = useNavigate();
  const { finishConversion } = useSession();
  const [credentials, setCredentials] = useCredentials();
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [showTosError, setShowTosError] = useState(false);
  const [convertedEmail, setConvertedEmail] = useState("");
  const [resendKey, setResendKey] = useState(0);

  const success = phase === "success";

  const canSubmit =
    emailLooksValid(credentials.email) &&
    passwordLongEnough(credentials.password) &&
    phase !== "submitting";

  async function submit() {
    if (!credentials.tosAccepted) {
      setShowTosError(true);
      return;
    }
    setShowTosError(false);
    setPhase("submitting");
    setError(null);

    try {
      /**
       * `returnTo` travels with the conversion so the **verification mail**
       * can carry it too (QA T-175 N2). The CTA's own URL only helps the
       * player who stays in this tab; the one who converts on a phone, closes
       * it and taps the link in their mail app takes the other road out of the
       * same panel, and the copy promises both of them the same landing. The
       * server sanitises it — this is a hint about where the player was, not an
       * instruction the API trusts.
       */
      const result = await convertGuest(credentials.email, credentials.password, true, returnTo);
      setConvertedEmail(result.email);
      setPhase("success");
      /**
       * Note what is NOT here any more: `await reload()`.
       *
       * That call is the whole of T-172 finding #2. It re-read a session that
       * had just been revoked on purpose, the stage flipped to `anonymous`, and
       * the guard on the screen behind the sheet redirected to
       * `/login?next=%2Flobby` — under a modal, while the player was reading
       * "check your email". The session is resynced later, deliberately, by the
       * CTA below (§14.1 r2).
       */
    } catch (caught) {
      if (caught instanceof ConversionError && caught.reason === "email_in_use") {
        setPhase("email-taken");
        return;
      }
      setError(caught instanceof Error ? caught.message : t("error.unexpected"));
      setPhase("idle");
    }
  }

  /**
   * The handoff: **go there, and get out of the way**, then resync.
   *
   * Three things happen here and each one is load-bearing.
   *
   * 1. **The route changes** to `/login?next=<captured target>` (r4).
   * 2. **The overlay comes down.** `OverlayProvider` mounts this sheet above
   *    `<Routes>` — deliberately, because §14 says converting is not a place
   *    you go — which means no navigation can ever unmount it. Only the state
   *    that mounted it can, and nothing was clearing that state on this path:
   *    QA T-175 B1, a full-viewport static scrim left over a login form the
   *    player could see and not touch, with r3 having (correctly) removed Esc,
   *    the scrim tap and every close affordance. There was no way out but a
   *    reload.
   * 3. **The session is resynced last.** The freeze is still up while the
   *    router commits, so no guard gets a chance to act on the stale
   *    "signed-in guest" the provider is still holding; `finishConversion`
   *    then re-reads the session (now genuinely signed out) and `RequireAnonymous`
   *    lowers the flag once the app has actually arrived. Resyncing first would
   *    put an `anonymous` session under a `/lobby` guard for one render and
   *    bounce the player somewhere they did not ask for.
   *
   * 1 and 2 share a `startTransition` so they are **one commit**. The router's
   * own update is a transition; a plain `setState` beside it is not, and React
   * would then commit them separately — one frame with the panel already gone
   * and the origin screen, unscrimmed and no longer authenticated, still on
   * screen. The player must never see that frame.
   */
  function handOffToLogin() {
    startTransition(() => {
      onHandoff();
      /**
       * **§14.2 item 7, LOCKED:** the target is
       * `/login?next=<captured>&email=<the address just submitted>`, plus an
       * `info` banner delivered by §8.2's survives-exactly-one-route-change
       * mechanism (router state, **not** a query parameter — a reload must
       * leave a clean login screen).
       *
       * The address travels so the player does not retype, ten seconds later
       * on a phone, the email they just handed over; `/login` consumes it on
       * mount and `replaceState`s it out of the URL, so it does not linger in
       * the address bar or in the history of a shared school laptop. The
       * password is never prefilled and never travels in a URL.
       *
       * The banner is the load-bearing half: without it we hand the player a
       * form that **cannot succeed** — they would type the password, tap LOG
       * IN, and be refused for a reason nothing on screen mentioned.
       */
      void navigate(withEmail(withNext("/login", returnTo), convertedEmail), {
        state: { banner: t("auth.banner.just_converted"), bannerTone: "info" },
      });
    });
    void finishConversion();
  }

  return (
    <BottomSheet
      open
      onClose={onClose}
      label={success ? t("convert.success.title") : t("convert.title")}
      testId="conversion-sheet"
      // §14.1 r3 / r4, both LOCKED.
      dismissible={!success}
      expanded={success}
      restoreFocusTo={restoreFocusTo}
    >
      {success ? (
        <div
          className="wr-stack"
          style={{ gap: 16, alignItems: "center", textAlign: "center" }}
          data-testid="convert-success"
        >
          {/* §7's verify content, in §14.1 r4's order. */}
          <Mascot size={72} />
          <h2 className="wr-display-md">{t("convert.success.title")}</h2>
          {/*
            LOCKED order inside this one string: the data moved across → you
            are signed out → verify, log in, and you come straight back here.
          */}
          <p className="wr-body-md wr-ink-70" data-testid="convert-success-body">
            {t("convert.success.body")}
          </p>
          {/* Its own mono line so a typo in the address is catchable (§7). */}
          <p className="wr-mono-md" data-testid="convert-success-email">
            {convertedEmail}
          </p>

          <CountdownAction
            key={resendKey}
            seconds={60}
            size="md"
            variant="secondary"
            fullWidth
            label={t("verify.resend")}
            waitingLabel={() => t("verify.resendWait")}
            testId="convert-resend"
            onAction={() => {
              // The same mail, so the same promise: a resent link lands where
              // the first one would have (T-175 N2). `returnTo` is the captured
              // conversion target, which §14.2 item 5 ranks above any `?next=`
              // in a URL — this panel is the interruption.
              void resendVerification(convertedEmail, returnTo).catch(() => undefined);
              setResendKey((value) => value + 1);
            }}
          />

          {/*
            The only exit. No secondary "keep playing" escape hatch — there is
            nothing to keep playing on (§14.1 r4, LOCKED).
          */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            data-testid="convert-login"
            onClick={handOffToLogin}
          >
            {t("convert.success.cta")}
          </Button>
        </div>
      ) : (
        <form
          className="wr-stack"
          style={{ gap: 16 }}
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) void submit();
          }}
        >
          <h2 className="wr-display-md">{t("convert.title")}</h2>

          <ul className="wr-stack" style={{ gap: 8, listStyle: "none", margin: 0, padding: 0 }}>
            {[
              t("convert.benefit.identity"),
              t("convert.benefit.coins"),
              t("convert.benefit.devices"),
            ].map((benefit) => (
              <li key={benefit} className="wr-row wr-body-md">
                <CheckIcon size={16} />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          {phase === "email-taken" ? (
            <FormBanner
              tone="info"
              testId="convert-email-taken"
              actions={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onClose();
                    void navigate("/login");
                  }}
                >
                  {t("convert.loginInstead")}
                </Button>
              }
            >
              {t("convert.emailTaken")}
            </FormBanner>
          ) : null}

          {error ? (
            <FormBanner tone="error" testId="convert-error">
              {error}
            </FormBanner>
          ) : null}

          <CredentialsFields
            idPrefix="convert"
            mode="signup"
            state={credentials}
            onChange={setCredentials}
            submitting={phase === "submitting"}
            showTosError={showTosError}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={!canSubmit}
            loading={phase === "submitting"}
            loadingLabel={t("common.working")}
            data-testid="convert-submit"
          >
            {t("convert.cta")}
          </Button>

          {/*
            T-234. A conversion creates a registered account, so it carries the
            same 13+ attestation as the sign-up tab — under the submit, which is
            where the auth screen's copy of it sits relative to its own primary
            CTA. Both links open in a new tab, so reading them does not abandon
            a half-filled sheet (§14 rule 3 makes abandonment free, not free of
            consequence: the typed password is gone with it).
          */}
          <LegalAttestation testId="convert-legal" />
        </form>
      )}
    </BottomSheet>
  );
}

/**
 * `&email=` onto a URL that may or may not already carry `?next=`.
 *
 * Spelled out rather than string-concatenated onto `withNext(...)`'s result:
 * `withNext` returns a bare `/login` when there is no captured target, and
 * `"/login" + "&email=…"` is a URL with no query string at all — a silent way
 * to lose the prefill on exactly the path that has least context to spare.
 */
function withEmail(path: string, email: string): string {
  if (!email) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}email=${encodeURIComponent(email)}`;
}
